import type { ShortlistBoardApi } from '../../../api/shortlist-board/shortlistBoard.api';
import type { WithServices } from '../../../platform/ts/services';
import type { WithErrorService } from '../../front/ErrorService/ErrorService';
import type { WithMessageService } from '../../internal/MessageService/MessageService';
import type { WithMetastructureMappingService } from '../../internal/MetastructureMappingService/MetastructureMappingService';
import type { WithTagService } from '../TagsService/TagsService';
import type { ShortlistBoardMutationService } from './ShortlistBoardMutationService';

/**
 * No v1 action follows these writes: the v1 store keeps whole shortlists with
 * their blocks, which these endpoints don't return. The v4 shortlist cache reads
 * again from the system effect (`reconcileShortlistsWithSystemEffects`): the
 * server's event for a block write is one it doesn't read.
 */
export function createShortlistBoardMutationService(
  config: WithServices<
    [
      WithMessageService,
      WithErrorService,
      WithTagService,
      WithMetastructureMappingService
    ]
  > & {
    api: ShortlistBoardApi;
  }
): ShortlistBoardMutationService {
  const {
    api,
    services: {
      messageService,
      errorService,
      tagsService,
      metastructureMappingService
    }
  } = config;
  const interceptor = errorService.withCategory(
    'ShortlistBoardMutationService'
  );

  async function written<T>(shortlistId: number, result: T): Promise<T> {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope: [
          {
            entity: 'shortlist',
            entityId: shortlistId,
            payload: { type: 'update' }
          }
        ]
      },
      3000
    );
    return result;
  }

  return {
    addContacts: interceptor.wrap(async (shortlist, request) =>
      written(
        shortlist.id,
        await api.addContacts(
          shortlist.publicId,
          await metastructureMappingService.getAllFieldNameTypeMappings(),
          request
        )
      )
    ),
    removeBlocks: interceptor.wrap(async (shortlist, request) =>
      written(
        shortlist.id,
        await api.removeBlocks(
          shortlist.publicId,
          await metastructureMappingService.getAllFieldNameTypeMappings(),
          request
        )
      )
    ),
    moveBlocks: interceptor.wrap(async (shortlist, request) =>
      written(shortlist.id, await api.moveBlocks(shortlist.publicId, request))
    ),
    updateSettings: interceptor.wrap(async (shortlistId, patch) =>
      written(shortlistId, await api.updateShortlist(shortlistId, patch))
    ),
    updateTags: interceptor.wrap(async (shortlistId, refs) => {
      const tags = await Promise.all(
        refs.map(ref =>
          ref.type === 'existing'
            ? tagsService.resolveById('shortlist', ref.id)
            : tagsService.addTag('shortlist', ref.label)
        )
      );
      return written(
        shortlistId,
        await api.updateShortlist(shortlistId, { tags })
      );
    }),
    updateBlockNote: interceptor.wrap(async (shortlistId, blockId, note) =>
      written(
        shortlistId,
        await api.updateBlockNote(shortlistId, blockId, note)
      )
    ),
    updateBlockPins: interceptor.wrap(async (shortlistId, blockId, pins) =>
      written(
        shortlistId,
        await api.updateBlockPins(shortlistId, blockId, pins)
      )
    )
  };
}
